import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import _ from "lodash";
import React, { useEffect } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";

import { POUND_SIGN } from "@/Constants/common";
import {
  getDayWiseRates,
  updateJobShiftObjFilters,
  updateJobShiftObjFromLocal,
  updateSelectedJobShiftObj,
  updateToggleSelected,
} from "@/reducers/clientcreatejob";
import { createJobValidations } from "@/utilities/CommonText";
import {
  isKeyUpValid,
  showErrorToast,
  validateTimeFormat,
} from "@/utilities/utils";

import JobShiftListsActions from "./JobShiftListsActions";
import CalendarSearch from "../dataTables/CalendarSearch";

/**
 * Function to render a list of job shifts and their associated details.
 *
 * @return {JSX.Element} The JSX element representing the job shift lists.
 */
export const JobShiftLists = () => {
  dayjs.extend(customParseFormat);
  const {
    selectedJobShiftObj,
    jobShiftObj,
    jobData,
    jobShiftObjFilters,
    toggleSelected,
    firstMultipleSelectedJobShiftObj,
    enableMultipleShiftEditing,
  } = useSelector((state) => state.clientCreateJob);
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedJobShiftObj.length === 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedJobShiftObj]);

  // check if rateCardList is an array or not
  const rateArray = Array.isArray(jobData?.rateCardList)
    ? jobData?.rateCardList
    : [jobData?.rateCardList];

  // check validation while edit the times. if it's fullfilled then only editable
  const _validateJobDateTimeAndRate =
    jobData.startDate &&
    jobData.endDate &&
    jobData.startTime &&
    jobData.endTime &&
    rateArray.length > 0;

  /**
   * Handle start and end time editing for the given date list and post object.
   *
   * @param {array} dateList - the list of dates to handle editing for
   * @param {object} postObj - the post object containing start and end time information
   * @return {void}
   */
  const handleStartEndTimeEditing = (postObj) => {
    const { selected_date, day, _startTime, _endTime, _breakTime, _adminTime } =
      postObj;
    dispatch(
      getDayWiseRates({
        fromEditing: true,
        day,
        selected_date,
        _startTime,
        _endTime,
        _breakTime,
        _adminTime: _adminTime ? _adminTime : "00:00",
      }),
    );
  };

  /**
   * Handles editing of admin time during breaks.
   *
   * @param {array} dateList - list of dates
   * @param {object} postObj - object containing post data
   * @return {void}
   */
  const handleBreakAdminTimeEditing = (dateList, postObj) => {
    dateList.map((date) => {
      dispatch(
        updateJobShiftObjFromLocal({
          ...postObj,
          fromTimeSelection: false,
          currentDate: date,
        }),
      );
    });
  };

  /**
   * Validates the time format.
   *
   * @param {string} time - The time to be validated.
   * @return {boolean} Returns true if the time is in the format "HH:mm", otherwise returns false.
   */

  const conditionalRowStyles = [
    {
      when: (row) => row.date === firstMultipleSelectedJobShiftObj.date,
      classNames: ["active"],
    },
    {
      when: (row) => selectedJobShiftObj.includes(row.date),
      classNames: ["selected"],
    },
  ];
  const columns = [
    {
      name: "Shift Date",
      /**
       * Generates a cell component for the given row.
       *
       * @param {type} row - The row object to generate the cell for
       * @return {type} The generated cell component
       */
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="inline-block text-[14px] font-medium leading-[normal]">
            {row.date}
          </span>
        </div>
      ),
    },
    {
      name: "Days",
      /**
       * A function that returns a JSX element representing a cell.
       *
       * @param {Object} row - the row object
       * @return {JSX.Element} a JSX element representing the cell
       */
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="text-[14px] font-medium leading-[normal]">
            {row.day}
          </span>
        </div>
      ),
    },
    {
      name: "Start",
      /**
       * Function to render the cell content for the given row.
       *
       * @param {type} row - description of parameter
       * @return {type} description of return value
       */
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="cursor-pointer text-[14px] font-medium leading-[normal]">
            {row.startTime ? (
              <>
                <div
                  contentEditable={
                    (_validateJobDateTimeAndRate &&
                      selectedJobShiftObj.length === 0) ||
                    (selectedJobShiftObj[0] === row.date &&
                      enableMultipleShiftEditing)
                      ? "true"
                      : "false"
                  }
                  className="border border-primary border-opacity-0 transition-all duration-200 ease-linear focus:inline-block focus:rounded-md focus:border-opacity-100 focus:bg-lightblue focus:px-4 focus:py-1 focus:text-primary focus:outline-none"
                  onKeyDownCapture={(e) => {
                    let curVal = e.target.innerText;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                    }
                    let isNumber = /^\d+$/.test(e.key);
                    if (isKeyUpValid(e.key)) {
                      e.preventDefault();
                    }
                    if (curVal.length === 5 && isNumber) {
                      e.preventDefault();
                    }
                  }}
                  onPasteCapture={(e) => {
                    e.preventDefault();
                  }}
                  onBlur={(e) => {
                    if (validateTimeFormat(e.target.innerText)) {
                      handleStartEndTimeEditing({
                        selected_date: [row.date],
                        day: row.day,
                        _startTime: e.target.innerText,
                        _endTime: row.endTime,
                        _breakTime: row.break,
                        _adminTime: row.admin,
                      });
                      // let isLessThanEndTime = dayjs(
                      //   e.target.innerText,
                      //   "HH:mm",
                      // ).isBefore(dayjs(row.endTime, "HH:mm"));
                      // if (isLessThanEndTime) {
                      //   handleStartEndTimeEditing({
                      //     selected_date: [row.date],
                      //     day: row.day,
                      //     _startTime: e.target.innerText,
                      //     _endTime: row.endTime,
                      //     _breakTime: row.break,
                      //     _adminTime: row.admin,
                      //   });
                      // } else {
                      //   showErrorToast(
                      //     "Start Time should be less than End Time",
                      //   );
                      //   e.target.innerText = row.startTime;
                      // }
                    } else {
                      if (e.target.innerText === "") {
                        showErrorToast(createJobValidations.emptyFields);
                      } else {
                        showErrorToast(createJobValidations.inValidTimeFormat);
                      }
                      e.target.innerText = row.startTime;
                    }
                  }}
                  suppressContentEditableWarning={true}
                >
                  {row.startTime}
                </div>
              </>
            ) : (
              "-"
            )}
          </span>
        </div>
      ),
    },
    {
      name: "End",
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="cursor-pointer text-[14px] font-medium leading-[normal]">
            {row.endTime ? (
              <>
                <div
                  contentEditable={
                    (_validateJobDateTimeAndRate &&
                      selectedJobShiftObj.length === 0) ||
                    (selectedJobShiftObj[0] === row.date &&
                      enableMultipleShiftEditing)
                      ? "true"
                      : "false"
                  }
                  className="border border-primary border-opacity-0 transition-all duration-200 ease-linear focus:inline-block focus:rounded-md focus:border-opacity-100 focus:bg-lightblue focus:px-4 focus:py-1 focus:text-primary focus:outline-none"
                  onKeyDownCapture={(e) => {
                    let curVal = e.target.innerText;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                    }
                    let isNumber = /^\d+$/.test(e.key);
                    if (isKeyUpValid(e.key)) {
                      e.preventDefault();
                    }
                    if (curVal.length === 5 && isNumber) {
                      e.preventDefault();
                    }
                  }}
                  onPasteCapture={(e) => {
                    e.preventDefault();
                  }}
                  onBlur={(e) => {
                    if (validateTimeFormat(e.target.innerText)) {
                      handleStartEndTimeEditing({
                        selected_date: [row.date],
                        day: row.day,
                        _startTime: row.startTime,
                        _endTime: e.target.innerText,
                        _breakTime: row.break,
                        _adminTime: row.admin,
                      });
                      // let isGreaterThanStartTime = dayjs(
                      //   e.target.innerText,
                      //   "HH:mm",
                      // ).isAfter(dayjs(row.startTime, "HH:mm"));
                      // if (isGreaterThanStartTime) {
                      // } else {
                      //   showErrorToast(
                      //     "End Time should be greater than Start Time",
                      //   );
                      //   e.target.innerText = row.endTime;
                      // }
                    } else {
                      if (e.target.innerText === "") {
                        showErrorToast(createJobValidations.emptyFields);
                      } else {
                        showErrorToast(createJobValidations.inValidTimeFormat);
                      }
                      e.target.innerText = row.startTime;
                    }
                  }}
                  suppressContentEditableWarning={true}
                >
                  {row.endTime}
                </div>
              </>
            ) : (
              "-"
            )}
          </span>
        </div>
      ),
    },

    {
      name: "Break",
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="text-[14px] font-medium leading-[normal]">
            {row.break ? (
              <>
                <div
                  contentEditable={
                    (_validateJobDateTimeAndRate &&
                      selectedJobShiftObj.length === 0) ||
                    (selectedJobShiftObj[0] === row.date &&
                      enableMultipleShiftEditing)
                      ? jobData.startDate &&
                        jobData.endDate &&
                        jobData.startTime &&
                        jobData.endTime &&
                        rateArray.length > 0
                        ? "true"
                        : "false"
                      : "false"
                  }
                  className="border border-primary border-opacity-0 transition-all duration-200 ease-linear focus:inline-block focus:rounded-md focus:border-opacity-100 focus:bg-lightblue focus:px-4 focus:py-1 focus:text-primary focus:outline-none"
                  onKeyDownCapture={(e) => {
                    let curVal = e.target.innerText;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                    }
                    let isNumber = /^\d+$/.test(e.key);
                    if (isKeyUpValid(e.key)) {
                      e.preventDefault();
                    }
                    if (curVal.length === 5 && isNumber) {
                      e.preventDefault();
                    }
                  }}
                  onPasteCapture={(e) => {
                    e.preventDefault();
                  }}
                  onBlur={(e) => {
                    if (validateTimeFormat(e.target.innerText)) {
                      let startEndDiff = dayjs(row.endTime, "HH:mm").diff(
                        dayjs(row.startTime, "HH:mm"),
                        "minute",
                      );
                      let breakTimeDiff = dayjs(e.target.innerText, "HH:mm")
                        .add(30, "minute")
                        .diff(dayjs("00:00", "HH:mm"), "minute");
                      if (breakTimeDiff > startEndDiff) {
                        showErrorToast(createJobValidations.maxBreakTime, {
                          id: "maxBreakTime",
                        });
                        e.target.innerText = row.break;
                        return;
                      }
                      // let isGreaterThanHour = dayjs(
                      //   e.target.innerText,
                      //   "HH:mm",
                      // ).isAfter(dayjs("01:00", "HH:mm")); // max break time is 1 hour
                      handleBreakAdminTimeEditing([row.date], {
                        break: e.target.innerText,
                        admin: row.admin,
                        startTime: row.startTime,
                        endTime: row.endTime,
                      });
                      // if (!isGreaterThanHour) {
                      //   //
                      // } else {
                      //   showErrorToast("Break Time should be less than 1 hour");
                      //   e.target.innerText = row.break;
                      // }
                    } else {
                      if (e.target.innerText === "") {
                        showErrorToast(createJobValidations.emptyFields);
                      } else {
                        showErrorToast(createJobValidations.inValidTimeFormat);
                      }
                      e.target.innerText = row.startTime;
                    }
                  }}
                  suppressContentEditableWarning={true}
                >
                  {row.break}
                </div>
              </>
            ) : (
              "-"
            )}
          </span>
        </div>
      ),
    },

    {
      name: "Admin",
      /**
       * Function to render the cell with specific styling and content based on the given row data.
       *
       * @param {type} row - description of the row parameter
       * @return {type} description of the content to be rendered
       */
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="text-[14px] font-medium leading-[normal]">
            {row.admin ? (
              <div
                contentEditable={
                  (_validateJobDateTimeAndRate &&
                    selectedJobShiftObj.length === 0) ||
                  (selectedJobShiftObj[0] === row.date &&
                    enableMultipleShiftEditing)
                    ? jobData.startDate &&
                      jobData.endDate &&
                      jobData.startTime &&
                      jobData.endTime &&
                      rateArray.length > 0
                      ? "true"
                      : "false"
                    : "false"
                }
                className="border border-primary border-opacity-0 transition-all duration-200 ease-linear focus:inline-block focus:rounded-md focus:border-opacity-100 focus:bg-lightblue focus:px-4 focus:py-1 focus:text-primary focus:outline-none"
                onKeyDownCapture={(e) => {
                  let curVal = e.target.innerText;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                  }
                  let isNumber = /^\d+$/.test(e.key);
                  if (isKeyUpValid(e.key)) {
                    e.preventDefault();
                  }
                  if (curVal.length === 5 && isNumber) {
                    e.preventDefault();
                  }
                }}
                onPasteCapture={(e) => {
                  e.preventDefault();
                }}
                onBlur={(e) => {
                  if (validateTimeFormat(e.target.innerText)) {
                    // let isGreaterThanHour = dayjs(
                    //   e.target.innerText,
                    //   "HH:mm",
                    // ).isAfter(dayjs("01:00", "HH:mm"));
                    handleBreakAdminTimeEditing([row.date], {
                      break: row.break,
                      admin: e.target.innerText,
                      startTime: row.startTime,
                      endTime: row.endTime,
                    });
                    // if (!isGreaterThanHour) {
                    // } else {
                    //   showErrorToast("Admin Time should be less than 1 hour");
                    //   e.target.innerText = row.admin;
                    // }
                  } else {
                    if (e.target.innerText === "") {
                      showErrorToast(createJobValidations.emptyFields);
                    } else {
                      showErrorToast(createJobValidations.inValidTimeFormat);
                    }
                    e.target.innerText = row.startTime;
                  }
                }}
                suppressContentEditableWarning={true}
              >
                {row.admin}
              </div>
            ) : (
              "-"
            )}
          </span>
        </div>
      ),
    },
    {
      name: "Rate",
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          {row.rate_values &&
            row.rate_values.map((rateVal, index) => {
              const { rate } = rateVal;
              return (
                <span
                  className="text-[14px] font-medium leading-[normal]"
                  key={index}
                >
                  {Number(`${!rate ? "0" : rate}`).toFixed(2)}
                </span>
              );
            })}
        </div>
      ),
    },
    {
      name: "Unit",
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          {row.rate_values &&
            row.rate_values.map((rateVal, index) => {
              return (
                <span
                  className="text-[14px] font-medium leading-[normal]"
                  key={index}
                >
                  {Number(rateVal.unit).toFixed(2)}
                </span>
              );
            })}
        </div>
      ),
    },

    {
      name: "Total",
      selector: (row) => (
        <div className="flex max-w-full flex-col">
          <span className="text-[14px] font-medium leading-[normal]">
            {POUND_SIGN} {Number(`${row.total}`).toFixed(2)}
          </span>
        </div>
      ),
    },
  ];
  /**
   * Handle the change of shift dates and update the job shift object filters.
   *
   * @param {type} dates - description of parameter
   * @return {type} description of return value
   */
  const handleShiftDateChange = (dates) => {
    dispatch(
      updateJobShiftObjFilters({
        dates: dates,
      }),
    );
  };
  /**
   * A function to select criteria for a row.
   *
   * @param {type} row - the row to apply the criteria to
   * @return {type} the result of the criteria selection
   */
  const rowSelectCritera = (row) => {
    return _.includes(selectedJobShiftObj, row.date);
  };
  return (
    <div className="JobShiftLists_Wrapper mb-[55px]">
      <div className="h-full overflow-hidden rounded-[10px] bg-white shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="flex flex-col px-4 pt-[22px]">
          <div className="mb-6 flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[18px] md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <h4 className="m-0 text-xl font-bold leading-[normal] text-black">
                Shifts List
              </h4>
              <JobShiftListsActions />
            </div>
            <CalendarSearch
              highlightDates={jobShiftObj.map((date) => date.date)}
              val={jobShiftObjFilters.dates}
              handleChange={handleShiftDateChange}
              format="DD-MM-YYYY"
              actMonthIndex={
                jobData.startDate
                  ? dayjs(jobData.startDate, "DD-MM-YYYY").month()
                  : dayjs().month()
              }
            />
          </div>
          <div
            className={`JobShiftLists_DataTable ${selectedJobShiftObj.length > 0 ? "has-selected" : ""} ${enableMultipleShiftEditing ? "enable-multiple-shift-editing" : ""}`}
          >
            <DataTable
              columns={columns}
              data={
                jobShiftObjFilters.dates.length > 0
                  ? jobShiftObj.filter((date) => {
                      return jobShiftObjFilters.dates.includes(date.date);
                    })
                  : jobShiftObj
              }
              fixedHeader
              fixedHeaderScrollHeight="500px"
              selectableRows
              selectableRowsHighlight
              onSelectedRowsChange={(state) => {
                let selectedDates =
                  state.selectedRows.map((row) => row.date) || [];
                dispatch(updateSelectedJobShiftObj(selectedDates));
              }}
              conditionalRowStyles={conditionalRowStyles}
              clearSelectedRows={toggleSelected}
              // selectableRowSelected={rowSelectCritera}
            />
          </div>
        </div>
        <div className="flex h-[43px] items-center justify-end bg-white pe-9 shadow-[0px_1px_9px_0px_#00000026]">
          <h3 className="text-base font-bold text-black">
            Candidate Total Pay : {POUND_SIGN}{" "}
            {Number(`${jobData.totalCandidatePay}`).toFixed(2)}
          </h3>
        </div>
      </div>
    </div>
  );
};
